// Bureau Portal API Slice - Monitoring, Alerts, Events, Suppression
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const bureauApiSlice = createApi({
  reducerPath: 'bureauApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: [
    'Alert',
    'Event',
    'SuppressionProfile',
    'DeviceOverride',
    'SuppressionStats',
    'SuppressedAlertGroups',
    'SuppressedGroupDetail',
    'SuppressionHealth',
    'SuppressionDecisionLogs',
    'Task',
    'MyTasks',
    'CreatedTasks',
    'TaskHistory',
    'TaskComment',
    'TaskActivity',
    'TaskStats',
    'SupportTicket',
    'SupportMessage',
    'Notification',
    'NotificationCount',
    'Broadcast',
    'User',
    'Note',
    'NoteCount',
    'EventClientComment',
  ],
  endpoints: (builder) => ({
    // ========== MONITORING ENDPOINTS ==========
    fetchAlerts: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/alerts/',
        params: {
          include_suppressed: true,
          include_profile_context: true,
          ...params,
        },
      }),
      providesTags: ['Alert'],
    }),

    fetchAlertsByDevice: builder.query({
      query: (device_id) => `monitoring/alerts/device/${device_id}/`,
      providesTags: ['Alert'],
    }),

    // ========== EVENT MANAGEMENT ==========
    createEvent: builder.mutation({
      query: (eventData) => ({
        url: 'monitoring/events/',
        method: 'POST',
        body: eventData,
        formData: true,
      }),
      invalidatesTags: ['Event'],
    }),

    fetchEvents: builder.query({
      query: (queryString) =>
        queryString ? `monitoring/events/list/?${queryString}` : 'monitoring/events/list/',
      providesTags: ['Event'],
    }),

    fetchEventDetails: builder.query({
      query: (event_id) => `monitoring/events/${event_id}/`,
      providesTags: (result, error, id) => [{ type: 'Event', id }],
    }),

    // The notes a client has written on an event, oldest first. Read-only: clients
    // write them in their own portal and the bureau does not answer them here.
    fetchEventClientComments: builder.query({
      query: (event_id) => `monitoring/events/${event_id}/comments/`,
      providesTags: (result, error, id) => [{ type: 'EventClientComment', id }],
    }),

    // Get event statistics
    fetchEventStats: builder.query({
      query: () => 'monitoring/events/stats/',
      providesTags: ['Event'],
    }),

    // Export events
    exportEvents: builder.mutation({
      query: (params) => ({
        url: 'monitoring/events/export/',
        method: 'POST',
        body: params,
        responseHandler: 'blob',
      }),
    }),

    resolveEvent: builder.mutation({
      query: ({ id, comments }) => ({
        url: `monitoring/events/${id}/resolve/`,
        method: 'POST',
        body: { resolution_comments: comments },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Event', id }, 'Event', 'Alert'],
    }),

    assignEvent: builder.mutation({
      query: ({ id, userId }) => ({
        url: `monitoring/events/${id}/assign/`,
        method: 'POST',
        body: { user_id: userId },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Event', id }, 'Event'],
    }),

    updateEvent: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `monitoring/events/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Event', id }, 'Event'],
    }),

    // Flag an event (admin only)
    flagEvent: builder.mutation({
      query: ({ eventId, flag_reason }) => ({
        url: `monitoring/events/${eventId}/flag/`,
        method: 'POST',
        body: { flag_reason },
      }),
      invalidatesTags: (result, error, { eventId }) => [{ type: 'Event', id: eventId }, 'Event'],
    }),

    // Unflag an event (admin only)
    unflagEvent: builder.mutation({
      query: (eventId) => ({
        url: `monitoring/events/${eventId}/flag/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, eventId) => [{ type: 'Event', id: eventId }, 'Event'],
    }),

    // ========== ALERT ACTIONS ==========
    resolveAlert: builder.mutation({
      query: (formData) => ({
        url: 'monitoring/resolve-alert/',
        method: 'POST',
        body: formData,
        formData: true,
      }),
      // WebSocket broadcasts handle real-time alert state sync across clients.
      // Only invalidate Event cache since resolving may create/update events.
      invalidatesTags: ['Event'],
    }),

    updateAlertStatus: builder.mutation({
      query: ({ alert_id, status }) => ({
        url: 'monitoring/update-alert-status/',
        method: 'POST',
        body: { alert_id, status },
        headers: { 'Content-Type': 'application/json' },
      }),
      // WebSocket broadcasts handle real-time alert state sync
    }),

    investigateAlert: builder.mutation({
      query: ({ alert_id, investigation_notes }) => ({
        url: 'monitoring/investigate-alert/',
        method: 'POST',
        body: { alert_id, investigation_notes },
        headers: { 'Content-Type': 'application/json' },
      }),
      // WebSocket broadcasts handle real-time alert state sync
    }),

    discardAlertConsolidated: builder.mutation({
      query: ({ alert_id, discard_reason, discard_notes }) => ({
        url: 'monitoring/discard-alert-consolidated/',
        method: 'POST',
        body: { alert_id, discard_reason, discard_notes },
        headers: { 'Content-Type': 'application/json' },
      }),
      // WebSocket broadcasts handle real-time alert state sync
    }),

    // ========== SUPPRESSION MANAGEMENT ==========
    fetchSuppressedAlerts: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppressed-alerts/',
        params: {
          page: params.page || 1,
          page_size: params.page_size || 20,
          ...params,
        },
      }),
      providesTags: ['Alert'],
    }),

    fetchSuppressedAlertDetails: builder.query({
      query: (alert_id) => `monitoring/suppressed-alerts/${alert_id}/`,
      providesTags: (result, error, id) => [{ type: 'Alert', id }],
    }),

    unsuppressAlert: builder.mutation({
      query: ({ alert_id, reason }) => ({
        url: 'monitoring/unsuppress-alert/',
        method: 'POST',
        body: { alert_id, reason },
        headers: { 'Content-Type': 'application/json' },
      }),
      // WebSocket broadcasts handle real-time alert state sync
    }),

    resetSuppressionGroup: builder.mutation({
      query: (data) => ({
        url: 'monitoring/reset-suppression-group/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Alert', 'SuppressedAlertGroups'],
    }),

    resolveSupressedAlert: builder.mutation({
      query: (data) => ({
        url: 'monitoring/resolve-suppressed-alert/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['Alert'],
    }),

    // ========== SUPPRESSION PROFILES ==========
    fetchUserSuppressionProfiles: builder.query({
      query: () => 'monitoring/user-suppression-profiles/',
      providesTags: ['SuppressionProfile'],
    }),

    fetchUserSuppressionProfile: builder.query({
      query: (profile_id) => `monitoring/user-suppression-profiles/${profile_id}/`,
      providesTags: (result, error, id) => [{ type: 'SuppressionProfile', id }],
    }),

    createUserSuppressionProfile: builder.mutation({
      query: (data) => ({
        url: 'monitoring/user-suppression-profiles/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['SuppressionProfile'],
    }),

    updateUserSuppressionProfile: builder.mutation({
      query: ({ profile_id, data }) => ({
        url: `monitoring/user-suppression-profiles/${profile_id}/`,
        method: 'PUT',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (result, error, { profile_id }) => [
        { type: 'SuppressionProfile', id: profile_id },
        'SuppressionProfile',
      ],
    }),

    deleteUserSuppressionProfile: builder.mutation({
      query: (profile_id) => ({
        url: `monitoring/user-suppression-profiles/${profile_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SuppressionProfile'],
    }),

    // ========== DEVICE OVERRIDES ==========
    fetchDeviceSuppressionOverrides: builder.query({
      query: () => 'monitoring/device-suppression-overrides/',
      providesTags: ['DeviceOverride'],
    }),

    fetchDeviceSuppressionOverride: builder.query({
      query: (override_id) => `monitoring/device-suppression-overrides/${override_id}/`,
      providesTags: (result, error, id) => [{ type: 'DeviceOverride', id }],
    }),

    createDeviceSuppressionOverride: builder.mutation({
      query: (data) => ({
        url: 'monitoring/device-suppression-overrides/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['DeviceOverride'],
    }),

    updateDeviceSuppressionOverride: builder.mutation({
      query: ({ override_id, data }) => ({
        url: `monitoring/device-suppression-overrides/${override_id}/`,
        method: 'PUT',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: (result, error, { override_id }) => [
        { type: 'DeviceOverride', id: override_id },
        'DeviceOverride',
      ],
    }),

    deleteDeviceSuppressionOverride: builder.mutation({
      query: (override_id) => ({
        url: `monitoring/device-suppression-overrides/${override_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DeviceOverride'],
    }),

    // ========== SUPPRESSION ANALYTICS ==========
    fetchSuppressionEffectivenessStats: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppression-effectiveness-stats/',
        params,
      }),
      providesTags: ['SuppressionStats'],
    }),

    fetchSuppressionStats: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppression-stats/',
        params,
      }),
      providesTags: ['SuppressionStats'],
    }),

    fetchUnifiedSuppressionStats: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/unified-suppression-stats/',
        params,
      }),
      providesTags: ['SuppressionStats'],
    }),

    fetchSuppressionHealth: builder.query({
      query: () => 'monitoring/suppression-health/',
      providesTags: ['SuppressionHealth'],
    }),

    testSuppressionRules: builder.mutation({
      query: (data) => ({
        url: 'monitoring/test-suppression-rules/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    fetchSuppressionDecisionLogs: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppression-decision-logs/',
        params,
      }),
      providesTags: ['SuppressionDecisionLogs'],
    }),

    // ========== ALERT GROUPING ==========
    fetchSuppressedAlertGroups: builder.query({
      query: (params = {}) => ({
        url: 'monitoring/suppressed-alerts/groups/',
        params: {
          page: params.page || 1,
          page_size: params.page_size || 5,
          use_cache: params.use_cache !== false,
          ...params,
        },
      }),
      providesTags: ['SuppressedAlertGroups'],
    }),

    fetchSuppressedGroupDetail: builder.query({
      query: ({ group_key, ...params }) => ({
        url: `monitoring/suppressed-alerts/groups/${group_key}/`,
        params: {
          page: params.page || 1,
          page_size: params.page_size || 50,
          use_cache: params.use_cache !== false,
          ...params,
        },
      }),
      providesTags: (result, error, { group_key }) => [
        { type: 'SuppressedGroupDetail', id: group_key },
      ],
    }),

    suppressedGroupBulkAction: builder.mutation({
      query: (data) => ({
        url: 'monitoring/suppressed-alerts/groups/bulk-action/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['SuppressedAlertGroups', 'Alert', 'SuppressedGroupDetail'],
    }),

    bulkDiscardSuppressedGroup: builder.mutation({
      query: (data) => ({
        url: 'monitoring/suppressed-alerts/groups/discard/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
      invalidatesTags: ['SuppressedAlertGroups', 'Alert', 'SuppressedGroupDetail'],
    }),

    validateSuppressedGroup: builder.query({
      query: (groupKey) => ({
        url: `monitoring/suppressed-alerts/groups/${encodeURIComponent(groupKey)}/`,
        method: 'GET',
      }),
      providesTags: (result, error, groupKey) => [{ type: 'SuppressedGroupDetail', id: groupKey }],
    }),

    validateSuppressedAlerts: builder.mutation({
      query: (data) => ({
        url: 'monitoring/suppressed-alerts/validate/',
        method: 'POST',
        body: data,
        headers: { 'Content-Type': 'application/json' },
      }),
    }),

    // ========== TASK MANAGEMENT ==========
    fetchTasks: builder.query({
      query: (params = {}) => ({
        url: 'operations/tasks-v2/',
        params,
      }),
      providesTags: ['Task'],
    }),

    fetchMyTasks: builder.query({
      query: (params = {}) => ({
        url: 'operations/tasks-v2/assigned_to_me/',
        params,
      }),
      providesTags: ['Task', 'MyTasks'],
    }),

    fetchCreatedTasks: builder.query({
      query: (params = {}) => ({
        url: 'operations/tasks-v2/created_by_me/',
        params,
      }),
      providesTags: ['Task', 'CreatedTasks'],
    }),

    fetchTaskHistory: builder.query({
      query: (params = {}) => ({
        url: 'operations/tasks-v2/history/',
        params,
      }),
      providesTags: ['TaskHistory'],
    }),

    fetchTaskDetail: builder.query({
      query: (taskId) => `operations/tasks-v2/${taskId}/`,
      providesTags: (result, error, id) => [{ type: 'Task', id }],
    }),

    fetchTaskStats: builder.query({
      query: () => 'operations/tasks-v2/stats/',
      providesTags: ['TaskStats'],
    }),

    createTask: builder.mutation({
      query: (data) => ({
        url: 'operations/tasks-v2/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Task', 'MyTasks', 'CreatedTasks', 'TaskStats'],
    }),

    updateTask: builder.mutation({
      query: ({ taskId, ...data }) => ({
        url: `operations/tasks-v2/${taskId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId },
        'Task',
        'TaskStats',
      ],
    }),

    deleteTask: builder.mutation({
      query: (taskId) => ({
        url: `operations/tasks-v2/${taskId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Task', 'MyTasks', 'CreatedTasks', 'TaskStats'],
    }),

    acceptTask: builder.mutation({
      query: (taskId) => ({
        url: `operations/tasks-v2/${taskId}/accept/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, taskId) => [
        { type: 'Task', id: taskId },
        'Task',
        'MyTasks',
        'TaskStats',
      ],
    }),

    declineTask: builder.mutation({
      query: ({ taskId, reason }) => ({
        url: `operations/tasks-v2/${taskId}/decline/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId },
        'Task',
        'MyTasks',
        'TaskStats',
      ],
    }),

    startTask: builder.mutation({
      query: (taskId) => ({
        url: `operations/tasks-v2/${taskId}/start/`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, taskId) => [
        { type: 'Task', id: taskId },
        'Task',
        'TaskStats',
      ],
    }),

    completeTask: builder.mutation({
      query: (taskId) => ({
        url: `operations/tasks-v2/${taskId}/complete/`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, taskId) => [
        { type: 'Task', id: taskId },
        'Task',
        'TaskHistory',
        'TaskStats',
      ],
    }),

    archiveTask: builder.mutation({
      query: (taskId) => ({
        url: `operations/tasks-v2/${taskId}/archive/`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Task', 'TaskHistory', 'TaskStats'],
    }),

    reassignTask: builder.mutation({
      query: ({ taskId, assigned_to }) => ({
        url: `operations/tasks-v2/${taskId}/reassign/`,
        method: 'POST',
        body: { assigned_to },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'Task', id: taskId },
        'Task',
        'TaskStats',
      ],
    }),

    fetchTaskComments: builder.query({
      query: (taskId) => `operations/tasks-v2/${taskId}/comments/`,
      providesTags: (result, error, taskId) => [{ type: 'TaskComment', id: taskId }],
    }),

    addTaskComment: builder.mutation({
      query: ({ taskId, comment }) => ({
        url: `operations/tasks-v2/${taskId}/add_comment/`,
        method: 'POST',
        body: { comment },
      }),
      invalidatesTags: (result, error, { taskId }) => [
        { type: 'TaskComment', id: taskId },
        { type: 'Task', id: taskId },
      ],
    }),

    fetchTaskActivity: builder.query({
      query: (taskId) => `operations/tasks-v2/${taskId}/activity/`,
      providesTags: (result, error, taskId) => [{ type: 'TaskActivity', id: taskId }],
    }),

    // ========== SUPPORT TICKET MANAGEMENT ==========
    fetchSupportTickets: builder.query({
      query: (params = {}) => ({
        url: 'operations/support-tickets/',
        params,
      }),
      providesTags: ['SupportTicket'],
    }),

    fetchSupportTicketDetail: builder.query({
      query: (ticketId) => `operations/support-tickets/${ticketId}/`,
      providesTags: (result, error, id) => [{ type: 'SupportTicket', id }],
    }),

    createSupportTicket: builder.mutation({
      query: (data) => ({
        url: 'operations/support-tickets/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SupportTicket'],
    }),

    addSupportMessage: builder.mutation({
      query: ({ ticketId, message }) => ({
        url: `operations/support-tickets/${ticketId}/add_message/`,
        method: 'POST',
        body: { message },
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: 'SupportTicket', id: ticketId },
        'SupportTicket',
      ],
    }),

    updateSupportTicket: builder.mutation({
      query: ({ ticketId, ...data }) => ({
        url: `operations/support-tickets/${ticketId}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: 'SupportTicket', id: ticketId },
        'SupportTicket',
      ],
    }),

    markSupportMessagesRead: builder.mutation({
      query: (ticketId) => ({
        url: `operations/support-tickets/${ticketId}/mark_messages_read/`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, ticketId) => [{ type: 'SupportTicket', id: ticketId }],
    }),

    // ========== TICKET ASSIGNMENT ENDPOINTS ==========
    assignTicket: builder.mutation({
      query: ({ ticketId, agentId, reason }) => ({
        url: `operations/support-tickets/${ticketId}/assign/`,
        method: 'POST',
        body: { agent_id: agentId, reason },
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: 'SupportTicket', id: ticketId },
        'SupportTicket',
        'MyAssignedTickets',
      ],
    }),

    claimTicket: builder.mutation({
      query: (ticketId) => ({
        url: `operations/support-tickets/${ticketId}/claim/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, ticketId) => [
        { type: 'SupportTicket', id: ticketId },
        'SupportTicket',
        'MyAssignedTickets',
      ],
    }),

    reassignTicket: builder.mutation({
      query: ({ ticketId, agentId, reason }) => ({
        url: `operations/support-tickets/${ticketId}/reassign/`,
        method: 'POST',
        body: { agent_id: agentId, reason },
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: 'SupportTicket', id: ticketId },
        'SupportTicket',
        'MyAssignedTickets',
      ],
    }),

    escalateTicket: builder.mutation({
      query: ({ ticketId, reason }) => ({
        url: `operations/support-tickets/${ticketId}/escalate/`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { ticketId }) => [
        { type: 'SupportTicket', id: ticketId },
        'SupportTicket',
        'MyAssignedTickets',
      ],
    }),

    // Fetch tickets assigned to the current support agent
    fetchMyAssignedTickets: builder.query({
      query: (params = {}) => ({
        url: 'operations/support-tickets/my-assigned/',
        params,
      }),
      providesTags: ['MyAssignedTickets', 'SupportTicket'],
    }),

    // Fetch available support agents with workload
    fetchAvailableSupportAgents: builder.query({
      query: () => 'operations/support-tickets/available-agents/',
      providesTags: ['SupportAgent'],
    }),

    // Update support agent configuration
    updateSupportAgent: builder.mutation({
      query: (agentData) => ({
        url: 'operations/support-tickets/update-agent/',
        method: 'POST',
        body: agentData,
      }),
      invalidatesTags: ['SupportAgent'],
    }),

    // Support Portal Dashboard Stats
    fetchSupportDashboardStats: builder.query({
      query: () => 'operations/support-tickets/dashboard_stats/',
      providesTags: ['SupportTicket', 'Broadcast', 'Notification'],
    }),

    // ========== NOTIFICATION MANAGEMENT ==========
    fetchNotifications: builder.query({
      query: (params = {}) => ({
        url: 'operations/notifications/',
        params,
      }),
      providesTags: ['Notification'],
      // Force refetch to prevent stale data
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }),

    fetchNotificationDetail: builder.query({
      query: (notificationId) => `operations/notifications/${notificationId}/`,
      providesTags: (result, error, notificationId) => [
        { type: 'Notification', id: notificationId },
      ],
    }),

    fetchUnreadNotificationCount: builder.query({
      query: () => 'operations/notifications/unread_count/',
      providesTags: ['NotificationCount'],
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
    }),

    markNotificationRead: builder.mutation({
      query: (notificationId) => ({
        url: `operations/notifications/${notificationId}/mark_read/`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),

    markAllNotificationsRead: builder.mutation({
      query: () => ({
        url: 'operations/notifications/mark_all_read/',
        method: 'POST',
      }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),

    deleteNotification: builder.mutation({
      query: (notificationId) => ({
        url: `operations/notifications/${notificationId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),

    createManualNotification: builder.mutation({
      query: (data) => ({
        url: 'operations/notifications/create_manual/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Notification', 'NotificationCount'],
    }),

    fetchNotificationRecipients: builder.query({
      query: (notificationId) => `operations/notifications/${notificationId}/recipients/`,
    }),

    // ========== BROADCAST MANAGEMENT ==========
    fetchBroadcasts: builder.query({
      query: (params = {}) => ({
        url: 'operations/broadcasts/',
        params,
      }),
      providesTags: ['Broadcast'],
    }),

    fetchBroadcastDetail: builder.query({
      query: (broadcastId) => `operations/broadcasts/${broadcastId}/`,
      providesTags: (result, error, id) => [{ type: 'Broadcast', id }],
    }),

    fetchUrgentBroadcasts: builder.query({
      query: () => 'operations/broadcasts/urgent/',
      providesTags: ['Broadcast'],
      // Poll every minute for urgent broadcasts
      pollingInterval: 60000,
    }),

    fetchActiveBroadcast: builder.query({
      query: () => 'operations/broadcasts/active_broadcast/',
      providesTags: ['Broadcast'],
    }),

    createBroadcast: builder.mutation({
      query: (data) => ({
        url: 'operations/broadcasts/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Broadcast', 'Notification'],
    }),

    updateBroadcast: builder.mutation({
      query: ({ broadcastId, ...data }) => ({
        url: `operations/broadcasts/${broadcastId}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { broadcastId }) => [
        { type: 'Broadcast', id: broadcastId },
        'Broadcast',
      ],
    }),

    deleteBroadcast: builder.mutation({
      query: (broadcastId) => ({
        url: `operations/broadcasts/${broadcastId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Broadcast'],
    }),

    deleteSupportTicket: builder.mutation({
      query: (ticketId) => ({
        url: `operations/support-tickets/${ticketId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SupportTicket'],
    }),

    sendBroadcast: builder.mutation({
      query: (broadcastId) => ({
        url: `operations/broadcasts/${broadcastId}/send/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, broadcastId) => [
        { type: 'Broadcast', id: broadcastId },
        'Broadcast',
        'Notification',
      ],
    }),

    previewBroadcast: builder.query({
      query: (broadcastId) => `operations/broadcasts/${broadcastId}/preview/`,
    }),

    fetchBroadcastStats: builder.query({
      query: (broadcastId) => `operations/broadcasts/${broadcastId}/stats/`,
    }),

    fetchBroadcastRecipients: builder.query({
      query: (broadcastId) => `operations/broadcasts/${broadcastId}/recipients/`,
    }),

    acknowledgeBroadcast: builder.mutation({
      query: (broadcastId) => ({
        url: `operations/broadcasts/${broadcastId}/acknowledge/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, broadcastId) => [
        { type: 'Broadcast', id: broadcastId },
        'Notification',
      ],
    }),

    // ========== USER MANAGEMENT (for broadcast targeting) ==========
    fetchUsers: builder.query({
      query: (params = {}) => ({
        url: 'v1/rbac/users/',
        params,
      }),
      providesTags: ['User'],
    }),

    // ========== NOTES (SPOT CHECK NOTES) ==========
    // List notes with optional filters
    fetchNotes: builder.query({
      query: (params = {}) => ({
        url: 'fleet/notes/',
        params,
      }),
      providesTags: ['Note'],
    }),

    // Get a specific note
    fetchNoteDetail: builder.query({
      query: (noteId) => `fleet/notes/${noteId}/`,
      providesTags: (result, error, noteId) => [{ type: 'Note', id: noteId }],
    }),

    // Create a new note
    createNote: builder.mutation({
      query: (noteData) => ({
        url: 'fleet/notes/',
        method: 'POST',
        body: noteData,
      }),
      invalidatesTags: ['Note', 'NoteCount'],
    }),

    // Update a note
    updateNote: builder.mutation({
      query: ({ noteId, ...noteData }) => ({
        url: `fleet/notes/${noteId}/`,
        method: 'PUT',
        body: noteData,
      }),
      invalidatesTags: (result, error, { noteId }) => [{ type: 'Note', id: noteId }, 'Note'],
    }),

    // Delete a note
    deleteNote: builder.mutation({
      query: (noteId) => ({
        url: `fleet/notes/${noteId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Note', 'NoteCount'],
    }),

    // Resolve a note
    resolveNote: builder.mutation({
      query: (noteId) => ({
        url: `fleet/notes/${noteId}/resolve/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, noteId) => [
        { type: 'Note', id: noteId },
        'Note',
        'NoteCount',
      ],
    }),

    // Unresolve a note
    unresolveNote: builder.mutation({
      query: (noteId) => ({
        url: `fleet/notes/${noteId}/unresolve/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, noteId) => [
        { type: 'Note', id: noteId },
        'Note',
        'NoteCount',
      ],
    }),

    // Pin/unpin a note
    toggleNotePin: builder.mutation({
      query: (noteId) => ({
        url: `fleet/notes/${noteId}/pin/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, noteId) => [{ type: 'Note', id: noteId }, 'Note'],
    }),

    // Get notes for a specific entity
    fetchEntityNotes: builder.query({
      query: ({ entityType, entityId }) => `fleet/notes/entity/${entityType}/${entityId}/`,
      providesTags: (result, error, { entityType, entityId }) => [
        { type: 'NoteCount', id: `${entityType}-${entityId}` },
        'Note',
      ],
    }),

    // Bulk get note counts for tree indicators
    fetchBulkNoteCounts: builder.mutation({
      query: (entityIds) => ({
        url: 'fleet/notes/counts/',
        method: 'POST',
        body: entityIds,
      }),
    }),

    // ========== SYSTEM CAPACITY/HEALTH ==========
    fetchSystemCapacity: builder.query({
      query: () => 'system/bureau-capacity/',
    }),

    // Every number the dashboard's cards and charts show, as server-side
    // aggregates. The page used to derive these by downloading the whole device
    // tree: 11.94 MB and 3,581 queries taking 27.9 s on production, where roughly
    // one load in eight never finished. This is ~5 KB.
    fetchBureauDashboardSummary: builder.query({
      query: () => 'fleet/bureau/dashboard-summary/',
      providesTags: [{ type: 'DeviceGroup', id: 'SUMMARY' }],
    }),

    // The tree's top level: organisations and their counts, ~345 KB against the
    // 10.43 MB the page used to download to draw the same rows.
    fetchFleetTreeOrganizations: builder.query({
      query: () => 'fleet/tree/organizations/',
      providesTags: [{ type: 'DeviceGroup', id: 'ORG_INDEX' }],
    }),

    // Search across the whole fleet without holding it in the browser. The tree
    // used to filter 18,445 devices in JavaScript, which is the only reason it
    // needed them all.
    fetchFleetTreeSearch: builder.query({
      query: (term) => `fleet/tree/search/?q=${encodeURIComponent(term || '')}`,
      keepUnusedDataFor: 30,
    }),

    // The counts behind the first card. The card read a Redux slice only the
    // Alerts page wrote, so on a fresh dashboard it showed a confident zero.
    fetchBureauAlertStats: builder.query({
      query: (params = {}) =>
        `monitoring/alerts/stats/${params.hours ? `?hours=${params.hours}` : ''}`,
      providesTags: [{ type: 'Alert', id: 'STATS' }],
    }),

    // ========== EXTERNAL DEVICE SEARCH & IMPORT ==========
    searchExternalDevice: builder.mutation({
      query: (data) => ({
        url: 'fleet/devices/search-external/',
        method: 'POST',
        body: data,
      }),
    }),
    importExternalDevice: builder.mutation({
      query: (data) => ({
        url: 'fleet/devices/import-external/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['DeviceGroup'],
    }),
    createExternalDevice: builder.mutation({
      query: (data) => ({
        url: 'fleet/devices/create-external/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['DeviceGroup'],
    }),

    // ========== TEST WIZARD — DEVICE CHECKS ==========
    deviceVMSCheck: builder.mutation({
      query: (data) => ({
        url: 'testing/device-vms-check/',
        method: 'POST',
        body: data,
      }),
    }),
    deviceCMSCheck: builder.mutation({
      query: (data) => ({
        url: 'testing/device-cms-check/',
        method: 'POST',
        body: data,
      }),
    }),
    deviceVMSAlerts: builder.mutation({
      query: (data) => ({
        url: 'testing/device-vms-alerts/',
        method: 'POST',
        body: data,
      }),
    }),
    deviceAlertPoll: builder.query({
      query: (params) => ({
        url: 'testing/device-alert-poll/',
        params,
      }),
    }),
    assignDeviceAlert: builder.mutation({
      query: (data) => ({
        url: 'testing/assign-device-alert/',
        method: 'POST',
        body: data,
      }),
    }),
    deviceCMSAlertPoll: builder.mutation({
      query: (data) => ({
        url: 'testing/device-cms-alert-poll/',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

// Export hooks for Bureau Portal components
export const {
  // Alert Monitoring
  useFetchAlertsQuery,
  useFetchAlertsByDeviceQuery,

  // Event Management
  useCreateEventMutation,
  useFetchEventsQuery,
  useFetchEventDetailsQuery,
  useFetchEventClientCommentsQuery,
  useFetchEventStatsQuery,
  useExportEventsMutation,
  useResolveEventMutation,
  useAssignEventMutation,
  useUpdateEventMutation,
  useFlagEventMutation,
  useUnflagEventMutation,

  // Alert Actions
  useResolveAlertMutation,
  useUpdateAlertStatusMutation,
  useInvestigateAlertMutation,
  useDiscardAlertConsolidatedMutation,

  // Suppressed Alerts
  useFetchSuppressedAlertsQuery,
  useFetchSuppressedAlertDetailsQuery,
  useUnsuppressAlertMutation,
  useResetSuppressionGroupMutation,
  useResolveSupressedAlertMutation,

  // Suppression Profiles
  useFetchUserSuppressionProfilesQuery,
  useFetchUserSuppressionProfileQuery,
  useCreateUserSuppressionProfileMutation,
  useUpdateUserSuppressionProfileMutation,
  useDeleteUserSuppressionProfileMutation,

  // Device Overrides
  useFetchDeviceSuppressionOverridesQuery,
  useFetchDeviceSuppressionOverrideQuery,
  useCreateDeviceSuppressionOverrideMutation,
  useUpdateDeviceSuppressionOverrideMutation,
  useDeleteDeviceSuppressionOverrideMutation,

  // Suppression Analytics
  useFetchSuppressionEffectivenessStatsQuery,
  useFetchSuppressionStatsQuery,
  useFetchUnifiedSuppressionStatsQuery,
  useFetchSuppressionHealthQuery,
  useTestSuppressionRulesMutation,
  useFetchSuppressionDecisionLogsQuery,

  // Alert Grouping
  useFetchSuppressedAlertGroupsQuery,
  useFetchSuppressedGroupDetailQuery,
  useSuppressedGroupBulkActionMutation,
  useBulkDiscardSuppressedGroupMutation,
  useValidateSuppressedGroupQuery,
  useValidateSuppressedAlertsMutation,

  // Task Management
  useFetchTasksQuery,
  useFetchMyTasksQuery,
  useFetchCreatedTasksQuery,
  useFetchTaskHistoryQuery,
  useFetchTaskDetailQuery,
  useFetchTaskStatsQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useAcceptTaskMutation,
  useDeclineTaskMutation,
  useStartTaskMutation,
  useCompleteTaskMutation,
  useArchiveTaskMutation,
  useReassignTaskMutation,
  useFetchTaskCommentsQuery,
  useAddTaskCommentMutation,
  useFetchTaskActivityQuery,

  // Support Tickets
  useFetchSupportTicketsQuery,
  useFetchSupportTicketDetailQuery,
  useCreateSupportTicketMutation,
  useAddSupportMessageMutation,
  useUpdateSupportTicketMutation,
  useMarkSupportMessagesReadMutation,
  useFetchSupportDashboardStatsQuery,

  // Ticket Assignment
  useAssignTicketMutation,
  useClaimTicketMutation,
  useReassignTicketMutation,
  useEscalateTicketMutation,
  useFetchMyAssignedTicketsQuery,
  useFetchAvailableSupportAgentsQuery,
  useUpdateSupportAgentMutation,

  // Notifications
  useFetchNotificationsQuery,
  useFetchNotificationDetailQuery,
  useFetchUnreadNotificationCountQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
  useDeleteNotificationMutation,
  useCreateManualNotificationMutation,
  useFetchNotificationRecipientsQuery,

  // Broadcasts
  useFetchBroadcastsQuery,
  useFetchBroadcastDetailQuery,
  useFetchUrgentBroadcastsQuery,
  useFetchActiveBroadcastQuery,
  useCreateBroadcastMutation,
  useUpdateBroadcastMutation,
  useDeleteBroadcastMutation,
  useDeleteSupportTicketMutation,
  useSendBroadcastMutation,
  usePreviewBroadcastQuery,
  useFetchBroadcastStatsQuery,
  useFetchBroadcastRecipientsQuery,
  useAcknowledgeBroadcastMutation,

  // Users (for broadcast targeting)
  useFetchUsersQuery,

  // System Capacity
  useFetchSystemCapacityQuery,
  useFetchBureauDashboardSummaryQuery,
  useFetchFleetTreeOrganizationsQuery,
  useFetchFleetTreeSearchQuery,
  useFetchBureauAlertStatsQuery,

  // Notes (Spot Check Notes)
  useFetchNotesQuery,
  useFetchNoteDetailQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useResolveNoteMutation,
  useUnresolveNoteMutation,
  useToggleNotePinMutation,
  useFetchEntityNotesQuery,
  useFetchBulkNoteCountsMutation,

  // External Device Search, Import & Create
  useSearchExternalDeviceMutation,
  useImportExternalDeviceMutation,
  useCreateExternalDeviceMutation,

  // Test Wizard — Device Checks
  useDeviceVMSCheckMutation,
  useDeviceCMSCheckMutation,
  useDeviceVMSAlertsMutation,
  useLazyDeviceAlertPollQuery,
  useAssignDeviceAlertMutation,
  useDeviceCMSAlertPollMutation,
} = bureauApiSlice;
